/**
 * Generic Share Service
 * Provides utility functions for sharing content across different platforms
 * with fallback support for iOS and older browsers
 */

import html2canvas from 'html2canvas';

const ShareService = (function() {
  'use strict';

  /**
   * Determine the mobile operating system
   * @returns {String} 'iOS', 'Android', 'Windows Phone', or 'unknown'
   */
  function getMobileOS() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/windows phone/i.test(userAgent)) {
      return 'Windows Phone';
    }
    if (/android/i.test(userAgent)) {
      return 'Android';
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'iOS';
    }
    return 'unknown';
  }

  /**
   * Check if the Clipboard API is supported
   * @returns {Boolean}
   */
  function isClipboardSupported() {
    return !!(navigator.clipboard && typeof navigator.clipboard.writeText === 'function');
  }

  /**
   * Fallback copy method for browsers that don't support Clipboard API (e.g., iOS Safari)
   * @param {String} text - Text to copy
   * @returns {Boolean} Success status
   */
  function fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.top = '0';
    textArea.style.left = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    let successful = false;
    try {
      successful = document.execCommand('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
      successful = false;
    }

    document.body.removeChild(textArea);
    return successful;
  }

  /**
   * Check if copy functionality is available
   * @returns {Boolean}
   */
  function isCopyAvailable() {
    return isClipboardSupported() ||
           (typeof document.execCommand === 'function' && /iPad|iPhone|iPod/.test(navigator.userAgent));
  }

  /**
   * Check if clipboard supports writing images
   * @returns {Boolean}
   */
  function isClipboardImageSupported() {
    return !!(navigator.clipboard && navigator.clipboard.write && ClipboardItem);
  }

  /**
   * Ensure document is focused before clipboard operation
   * @returns {Promise<void>}
   */
  function ensureDocumentFocus() {
    return new Promise((resolve) => {
      if (document.hasFocus()) {
        resolve();
        return;
      }

      // Try to focus the window
      window.focus();

      // Give it a moment to focus
      setTimeout(() => {
        resolve();
      }, 100);
    });
  }

  /**
   * Copy text to clipboard with iOS fallback (optionally with image)
   * @param {String} text - Text to copy
   * @param {Object} options - Optional settings
   * @param {HTMLElement|String} options.element - Optional element to capture and copy as image
   * @param {Object} options.captureOptions - html2canvas options
   * @returns {Promise<Boolean>} Promise that resolves to success status
   */
  function copyToClipboard(text, options = {}) {
    if (!text && !options.element) {
      return Promise.reject(new Error('No text or element provided to copy'));
    }

    // Ensure document is focused before clipboard operation
    return ensureDocumentFocus().then(() => {
      // If element is provided and clipboard supports images, copy both text and image
      if (options.element && isClipboardImageSupported()) {
        return captureElementAsBlob(options.element, options.captureOptions)
          .then(blob => {
            // Create a data URL for the image to embed in HTML
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = function(e) {
                const imageDataUrl = e.target.result;

                // Create HTML that includes both text and image
                // The text is preserved and the image is embedded
                // Note: Using pre-wrap to preserve line breaks in the text
                const htmlContent = `
                  <div>
                    <p style="margin: 0 0 20px 0; white-space: pre-wrap; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">${(text || '').replace(/\n/g, '<br>')}</p>
                    <br>
                    <img src="${imageDataUrl}" style="max-width: 100%; height: auto; display: block; margin-top: 10px;" />
                  </div>
                `.trim();

                // Important: Do NOT include standalone 'image/png' format
                // If we include it, apps like iOS Messages will prioritize the image
                // and ignore the HTML (which has both text and image together)
                const textBlob = new Blob([text || ''], { type: 'text/plain' });
                const htmlBlob = new Blob([htmlContent], { type: 'text/html' });

                // Only include text/plain and text/html (with embedded image)
                // This ensures rich apps get BOTH text and image from the HTML
                const clipboardItem = new ClipboardItem({
                  'text/plain': textBlob,
                  'text/html': htmlBlob
                });

                navigator.clipboard.write([clipboardItem])
                  .then(() => resolve(true))
                  .catch(err => {
                    console.error('Clipboard write with image failed, trying text only:', err);
                    copyTextOnly(text).then(resolve).catch(reject);
                  });
              };
              reader.onerror = function() {
                reject(new Error('Failed to read image blob'));
              };
              reader.readAsDataURL(blob);
            });
          })
          .catch(err => {
            console.error('Image capture failed, copying text only:', err);
            return copyTextOnly(text);
          });
      }

      // Text-only copy
      return copyTextOnly(text);
    });
  }

  /**
   * Copy only text to clipboard
   * @param {String} text - Text to copy
   * @returns {Promise<Boolean>}
   */
  function copyTextOnly(text) {
    if (!text) {
      return Promise.reject(new Error('No text provided to copy'));
    }

    if (isClipboardSupported()) {
      return navigator.clipboard.writeText(text)
        .then(() => true)
        .catch(err => {
          console.error('Clipboard API failed:', err);
          // If clipboard API fails (e.g., not focused), try fallback
          if (err.name === 'NotAllowedError') {
            console.log('Trying fallback copy method...');
            return new Promise((resolve, reject) => {
              const success = fallbackCopyToClipboard(text);
              if (success) {
                resolve(true);
              } else {
                reject(new Error('Copy failed'));
              }
            });
          }
          return false;
        });
    } else {
      // Fallback for iOS and older browsers
      return new Promise((resolve, reject) => {
        const success = fallbackCopyToClipboard(text);
        if (success) {
          resolve(true);
        } else {
          reject(new Error('Copy failed'));
        }
      });
    }
  }

  /**
   * Check if native share is available
   * @returns {Boolean}
   */
  function isShareAvailable() {
    return !!(navigator.share && typeof navigator.share === 'function');
  }

  /**
   * Share using native share API (with optional image)
   * @param {Object} options - Share options
   * @param {String} options.title - Title to share
   * @param {String} options.text - Text to share
   * @param {String} options.url - URL to share
   * @param {HTMLElement|String} options.element - Optional element to capture as image
   * @param {String} options.fileName - File name for image (default: 'image.png')
   * @param {Object} options.captureOptions - html2canvas options
   * @returns {Promise<Boolean>} Promise that resolves to success status
   */
  function share(options = {}) {
    if (!isShareAvailable()) {
      return Promise.reject(new Error('Native share not available'));
    }

    // If element is provided, capture and share with image
    if (options.element) {
      return captureElementAsBlob(options.element, options.captureOptions)
        .then(blob => {
          const fileName = options.fileName || 'image.png';
          const file = new File([blob], fileName, { type: 'image/png' });

          const shareData = {
            files: [file]
          };

          if (options.title) shareData.title = options.title;
          if (options.text) shareData.text = options.text;
          if (options.url) shareData.url = options.url;

          // Check if we can share with files
          if (navigator.canShare && navigator.canShare(shareData)) {
            return navigator.share(shareData)
              .then(() => true)
              .catch(err => {
                if (err.name !== 'AbortError') {
                  console.error('Share with image failed:', err);
                }
                return false;
              });
          } else {
            // Fallback to text-only share if files not supported
            const textShareData = {};
            if (options.title) textShareData.title = options.title;
            if (options.text) textShareData.text = options.text;
            if (options.url) textShareData.url = options.url;

            return navigator.share(textShareData)
              .then(() => true)
              .catch(err => {
                if (err.name !== 'AbortError') {
                  console.error('Share failed:', err);
                }
                return false;
              });
          }
        })
        .catch(err => {
          // If image capture fails, fallback to text-only share
          console.warn('Image capture failed, falling back to text share:', err);
          const shareData = {};
          if (options.title) shareData.title = options.title;
          if (options.text) shareData.text = options.text;
          if (options.url) shareData.url = options.url;

          return navigator.share(shareData)
            .then(() => true)
            .catch(err => {
              if (err.name !== 'AbortError') {
                console.error('Share failed:', err);
              }
              return false;
            });
        });
    }

    // Text-only share
    const shareData = {};
    if (options.title) shareData.title = options.title;
    if (options.text) shareData.text = options.text;
    if (options.url) shareData.url = options.url;

    return navigator.share(shareData)
      .then(() => true)
      .catch(err => {
        // User cancelled or share failed
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
        return false;
      });
  }

  /**
   * Share via email
   * Note: Email URL schemes don't support images. Images can only be shared via native share API.
   * @param {Object} options - Email options
   * @param {String} options.subject - Email subject
   * @param {String} options.body - Email body
   */
  function shareEmail(options = {}) {
    const subject = encodeURIComponent(options.subject || '');
    const body = encodeURIComponent(options.body || '');
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  /**
   * Share via SMS
   * Note: SMS URL schemes don't support images. Images can only be shared via native share API.
   * @param {Object} options - SMS options
   * @param {String} options.body - SMS body text
   */
  function shareSMS(options = {}) {
    const body = encodeURIComponent(options.body || '');
    const os = getMobileOS();
    // iOS uses a different SMS URL format
    const smsUrl = os === 'iOS' ? `sms:&body=${body}` : `sms:?body=${body}`;
    window.location.href = smsUrl;
  }

  /**
   * Share on Facebook
   * @param {Object} options - Facebook share options
   * @param {String} options.url - URL to share
   * @param {String} options.quote - Quote text
   */
  function shareFacebook(options = {}) {
    const url = encodeURIComponent(options.url || window.location.href);
    const quote = encodeURIComponent(options.quote || '');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, '_blank');
  }

  /**
   * Share on Twitter
   * @param {Object} options - Twitter share options
   * @param {String} options.text - Tweet text
   * @param {String} options.url - URL to include in tweet
   */
  function shareTwitter(options = {}) {
    const text = options.text || '';
    const url = options.url || '';
    const tweetText = encodeURIComponent(url ? `${text} ${url}` : text);
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
  }

  /**
   * Capture an HTML element as a canvas/blob
   * @param {HTMLElement|String} element - Element or selector to capture
   * @param {Object} options - html2canvas options
   * @returns {Promise<Blob>} Promise that resolves to image blob
   */
  function captureElementAsBlob(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;

    if (!el) {
      return Promise.reject(new Error('Element not found'));
    }

    const defaultOptions = {
      backgroundColor: null,
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      ...options
    };

    const clonedEl = el.cloneNode(true);

    clonedEl.classList.add('capturing');

    const originalParent = el.parentNode;

    clonedEl.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      pointer-events: none;
      z-index: -1;
    `;
    originalParent.appendChild(clonedEl);

    return html2canvas(clonedEl, defaultOptions)
      .then(canvas => {
        // Remove the cloned element from DOM
        originalParent.removeChild(clonedEl);

        return new Promise((resolve, reject) => {
          canvas.toBlob(blob => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          }, 'image/png');
        });
      })
      .catch(err => {
        if (originalParent.contains(clonedEl)) {
          originalParent.removeChild(clonedEl);
        }
        throw err;
      });
  }

  /**
   * Capture an HTML element as a data URL
   * @param {HTMLElement|String} element - Element or selector to capture
   * @param {Object} options - html2canvas options
   * @returns {Promise<String>} Promise that resolves to data URL
   */
  function captureElementAsDataURL(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;

    if (!el) {
      return Promise.reject(new Error('Element not found'));
    }

    const defaultOptions = {
      backgroundColor: null,
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      ...options
    };

    // Create a copy of the element for capture to avoid modifying the original
    const clonedEl = el.cloneNode(true);

    // Add capturing class to the cloned element
    clonedEl.classList.add('capturing');

    // Get the original element's parent to maintain Alpine.js context
    const originalParent = el.parentNode;

    // Temporarily add the cloned element to the same parent (but hide it)
    // Use opacity instead of visibility to ensure images load
    clonedEl.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      opacity: 0;
      pointer-events: none;
      z-index: -1;
    `;
    originalParent.appendChild(clonedEl);

    return html2canvas(clonedEl, defaultOptions)
      .then(canvas => {
        // Remove the cloned element from DOM
        originalParent.removeChild(clonedEl);
        return canvas.toDataURL('image/png');
      })
      .catch(err => {
        // Ensure we remove the cloned element even if capture fails
        if (originalParent.contains(clonedEl)) {
          originalParent.removeChild(clonedEl);
        }
        throw err;
      });
  }


  /**
   * Download captured element as image
   * @param {Object} options - Download options
   * @param {HTMLElement|String} options.element - Element to capture
   * @param {String} options.fileName - File name (default: 'image.png')
   * @param {Object} options.captureOptions - html2canvas options
   * @returns {Promise<Boolean>} Promise that resolves to success status
   */
  function downloadElementAsImage(options = {}) {
    return captureElementAsDataURL(options.element, options.captureOptions)
      .then(dataURL => {
        const link = document.createElement('a');
        link.download = options.fileName || 'image.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
      })
      .catch(err => {
        console.error('Download failed:', err);
        return false;
      });
  }

  // Public API
  return {
    isCopyAvailable,
    isClipboardImageSupported,
    copyToClipboard,
    isShareAvailable,
    share,
    shareEmail,
    shareSMS,
    shareFacebook,
    shareTwitter,
    getMobileOS,
    captureElementAsBlob,
    captureElementAsDataURL,
    downloadElementAsImage,
  };
})();

// Make available globally
if (typeof window !== 'undefined') {
  window.ShareService = ShareService;
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ShareService;
}

// ES6 export
export default ShareService;

